import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package,
  Wallet,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ShoppingCart,
  ArrowRight,
  Heart,
  QrCode,
  Check,
  X,
  ThumbsUp,
  Coins,
  MessageSquare,
  Tag,
  Percent,
  MapPin,
  Megaphone,
  Plus,
  Trash2,
  Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const StatCard = ({ icon: Icon, title, value, subtitle, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className={`text-3xl font-heading font-bold mt-1 ${color}`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-md flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const ActivityItem = ({ item, type }) => {
  const isRefill = type === "refill";
  const Icon = isRefill ? RefreshCw : ShoppingCart;
  
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isRefill ? 'bg-emerald-100' : 'bg-rose-100'}`}>
        <Icon className={`w-5 h-5 ${isRefill ? 'text-emerald-600' : 'text-rose-600'}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {item.product_name}
        </p>
        <p className="text-sm text-slate-500">
          {isRefill ? `Fach ${item.slot_id} · +${item.quantity}` : `${item.quantity}x · ${item.total?.toFixed(2)}€`}
        </p>
      </div>
      <span className="text-xs text-slate-400 font-mono">
        {new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
      </span>
    </div>
  );
};

const WishItem = ({ wish, onUpdate }) => {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [assignedSlots, setAssignedSlots] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      // Parse slots (comma-separated)
      const slots = assignedSlots
        .split(",")
        .map(s => s.trim().toUpperCase())
        .filter(s => s);
      
      await axios.put(`${API}/wishes/${wish.id}`, { 
        status: "approved",
        assigned_slots: slots,
        admin_note: adminNote
      });
      toast.success("Wunsch genehmigt");
      setShowApproveDialog(false);
      onUpdate();
    } catch (err) {
      toast.error("Fehler");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      await axios.delete(`${API}/wishes/${wish.id}`);
      toast.success("Wunsch entfernt");
      onUpdate();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  // Determine rank color based on votes
  const getRankStyle = (votes) => {
    if (votes >= 5) return "bg-amber-500 text-white"; // Gold
    if (votes >= 3) return "bg-slate-400 text-white"; // Silver
    if (votes >= 2) return "bg-orange-700 text-white"; // Bronze
    return "bg-slate-200 text-slate-600";
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getRankStyle(wish.votes)}`}>
        {wish.votes || 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate text-sm">{wish.product_name}</p>
        {wish.comment && <p className="text-xs text-slate-500 truncate">{wish.comment}</p>}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-400 flex items-center gap-1">
          <Heart className="w-3 h-3" />
          {wish.votes || 1}
        </span>
        
        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" title="Genehmigen">
              <Check className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Wunsch genehmigen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{wish.product_name}</p>
                <p className="text-sm text-slate-500">{wish.votes || 1} Stimmen</p>
              </div>
              
              <div>
                <Label htmlFor="slots">In welchen Fächern verfügbar?</Label>
                <Input
                  id="slots"
                  placeholder="z.B. A1, B3, C2"
                  value={assignedSlots}
                  onChange={(e) => setAssignedSlots(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Kommagetrennt eingeben, z.B. "A1, B2"
                </p>
              </div>
              
              <div>
                <Label htmlFor="note">Notiz (optional)</Label>
                <Textarea
                  id="note"
                  placeholder="z.B. Ab sofort erhältlich!"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleApprove} 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? "..." : "Genehmigen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={handleReject} title="Entfernen">
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const QRCodeDialog = () => {
  // Use production URL instead of localhost
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://snackomat.luenefire.de';
  const wishUrl = `${baseUrl}/wunsch`;
  const votingUrl = `${baseUrl}/wuensche`;
  const problemUrl = `${baseUrl}/problem`;
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="qr-code-btn">
          <QrCode className="w-4 h-4 mr-2" />
          QR-Codes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>QR-Codes zum Ausdrucken</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-4">
          {/* Wish QR */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg shadow-inner border border-rose-200">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(wishUrl)}`}
                alt="Wunsch QR Code"
                className="w-24 h-24 sm:w-28 sm:h-28"
              />
            </div>
            <p className="text-xs font-medium text-rose-700 mt-2 text-center">
              Wunsch einreichen
            </p>
            <p className="text-[9px] text-slate-400 text-center truncate max-w-full">
              /wunsch
            </p>
          </div>
          
          {/* Voting QR */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg shadow-inner border border-emerald-200">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(votingUrl)}`}
                alt="Voting QR Code"
                className="w-24 h-24 sm:w-28 sm:h-28"
              />
            </div>
            <p className="text-xs font-medium text-emerald-700 mt-2 text-center">
              Abstimmen
            </p>
            <p className="text-[9px] text-slate-400 text-center truncate max-w-full">
              /wuensche
            </p>
          </div>
          
          {/* Problem QR */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-2 rounded-lg shadow-inner border border-amber-200">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(problemUrl)}`}
                alt="Problem QR Code"
                className="w-24 h-24 sm:w-28 sm:h-28"
              />
            </div>
            <p className="text-xs font-medium text-amber-700 mt-2 text-center">
              Problem melden
            </p>
            <p className="text-[9px] text-slate-400 text-center truncate max-w-full">
              /problem
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center pt-2 border-t">
          <p className="text-xs text-slate-500">
            QR-Codes am Automaten anbringen
          </p>
          <a href="/qr-codes-print.html" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <QrCode className="w-4 h-4 mr-2" />
              Druckversion
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProblemItem = ({ problem, onResolve, onReject }) => {
  const typeLabels = {
    money_lost: { label: "Geld weg", color: "bg-amber-100 text-amber-700" },
    empty_slot: { label: "Fach leer", color: "bg-rose-100 text-rose-700" },
    other: { label: "Sonstiges", color: "bg-blue-100 text-blue-700" }
  };
  
  const typeInfo = typeLabels[problem.problem_type] || typeLabels.other;
  
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
          <span className="text-sm font-medium truncate">{problem.reporter_name}</span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-1">
          {problem.amount > 0 && `${problem.amount.toFixed(2)}€ • `}
          {problem.slot_id && `Fach ${problem.slot_id} • `}
          {problem.description || "Keine Details"}
        </p>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => onResolve(problem.id)}>
          <Check className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => onReject(problem.id)}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [wishes, setWishes] = useState([]);
  const [problems, setProblems] = useState([]);
  const [discountOffers, setDiscountOffers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    message: "",
    type: "info",
    target_page: "wishes",
    valid_from: new Date().toISOString().split("T")[0],
    valid_until: ""
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Laden der Hauptdaten
      const [statsRes, wishesRes, problemsRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`),
        axios.get(`${API}/wishes`),
        axios.get(`${API}/problem-reports?status=open`)
      ]);
      setStats(statsRes.data);
      setWishes(wishesRes.data.filter(w => w.status === "open"));
      setProblems(problemsRes.data);
      
      // Discount-Offers separat laden (optional - falls Server nicht aktualisiert)
      try {
        const offersRes = await axios.get(`${API}/discount-offers/matched`);
        setDiscountOffers(offersRes.data.matched_offers || []);
      } catch (e) {
        // Ignorieren wenn Endpoint nicht existiert
        console.log("Discount offers not available");
      }
      
      // Ankündigungen laden
      try {
        const announcementsRes = await axios.get(`${API}/wish-announcements?active_only=false`);
        setAnnouncements(announcementsRes.data.announcements || []);
      } catch (e) {
        console.log("Announcements not available");
      }
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      await axios.post(`${API}/wish-announcements`, {
        ...announcementForm,
        valid_until: announcementForm.valid_until || null
      });
      toast.success("Ankündigung erstellt");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({
        title: "",
        message: "",
        type: "info",
        target_page: "wishes",
        valid_from: new Date().toISOString().split("T")[0],
        valid_until: ""
      });
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sitzung abgelaufen - bitte neu einloggen");
      } else {
        toast.error("Fehler beim Erstellen: " + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/wish-announcements/${id}`);
      toast.success("Ankündigung gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleAnnouncement = async (id, isActive) => {
    try {
      await axios.put(`${API}/wish-announcements/${id}`, { is_active: !isActive });
      toast.success(isActive ? "Ankündigung deaktiviert" : "Ankündigung aktiviert");
      fetchData();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const handleResolveProblem = async (id) => {
    try {
      await axios.put(`${API}/problem-reports/${id}/resolve`);
      toast.success("Problem als erledigt markiert");
      fetchData();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const handleRejectProblem = async (id) => {
    try {
      await axios.put(`${API}/problem-reports/${id}/reject`);
      toast.success("Problem abgelehnt");
      fetchData();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Übersicht über den Snackautomaten
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Wallet}
          title="Kassenbestand"
          value={`${stats?.cash_balance?.toFixed(2) || '0.00'}€`}
          subtitle={`+${stats?.revenue_today?.toFixed(2) || '0.00'}€ heute`}
          color="text-emerald-600"
          delay={0}
        />
        <StatCard
          icon={Package}
          title="Produkte"
          value={stats?.products_count || 0}
          subtitle={`${stats?.filled_compartments || 0} Fächer belegt`}
          color="text-blue-600"
          delay={0.1}
        />
        <StatCard
          icon={AlertTriangle}
          title="Niedriger Bestand"
          value={stats?.low_stock_count || 0}
          subtitle="Fächer nachfüllen"
          color="text-amber-600"
          delay={0.2}
        />
        <StatCard
          icon={TrendingUp}
          title="Umsatz heute"
          value={`${stats?.revenue_today?.toFixed(2) || '0.00'}€`}
          subtitle={`${stats?.sales_count_today || 0} Verkäufe`}
          color="text-rose-600"
          delay={0.3}
        />
      </div>

      {/* Activity Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Refills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Auffüllungen
              </CardTitle>
              <Link to="/auffuellen">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_refills?.length > 0 ? (
                <div>
                  {stats.recent_refills.map((item) => (
                    <ActivityItem key={item.id} item={item} type="refill" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Auffüllungen
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Verkäufe
              </CardTitle>
              <Link to="/berichte">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_sales?.length > 0 ? (
                <div>
                  {stats.recent_sales.map((item) => (
                    <ActivityItem key={item.id} item={item} type="sale" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Verkäufe
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Product Wishes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.55 }}
      >
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Produktwünsche
              {wishes.length > 0 && (
                <span className="bg-rose-100 text-rose-600 text-xs px-2 py-0.5 rounded-full">
                  {wishes.length}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Link to="/wuensche" target="_blank">
                <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700" data-testid="public-voting-link">
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Öffentliche Abstimmung
                </Button>
              </Link>
              <QRCodeDialog />
            </div>
          </CardHeader>
          <CardContent>
            {wishes.length > 0 ? (
              <>
                <div className="max-h-64 overflow-y-auto">
                  {wishes.map((wish) => (
                    <WishItem key={wish.id} wish={wish} onUpdate={fetchData} />
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                  <p className="text-xs text-slate-500">
                    Sortiert nach Stimmen • Nutzer können über <span className="font-mono text-emerald-600">/wuensche</span> abstimmen
                  </p>
                </div>
              </>
            ) : (
              <p className="text-slate-500 text-center py-6">
                Keine offenen Wünsche
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Problem Reports */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <Card className="border-slate-200 border-amber-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Problemmeldungen
              {problems.length > 0 && (
                <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full">
                  {problems.length}
                </span>
              )}
            </CardTitle>
            <QRCodeDialog />
          </CardHeader>
          <CardContent>
            {problems.length > 0 ? (
              <div className="max-h-64 overflow-y-auto">
                {problems.map((problem) => (
                  <ProblemItem 
                    key={problem.id} 
                    problem={problem} 
                    onResolve={handleResolveProblem}
                    onReject={handleRejectProblem}
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6">
                Keine offenen Meldungen
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Discount Offers */}
      {discountOffers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.62 }}
        >
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Tag className="w-5 h-5 text-emerald-600" />
                Aktuelle Angebote
                <span className="bg-emerald-100 text-emerald-600 text-xs px-2 py-0.5 rounded-full">
                  {discountOffers.length}
                </span>
              </CardTitle>
              <Link to="/angebote">
                <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
                  Alle verwalten <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {discountOffers.slice(0, 5).map((item) => (
                  <div key={item.offer.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-emerald-100">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate text-sm">{item.product.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs bg-slate-100">
                          {item.offer.discounter}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          bis {new Date(item.offer.valid_until).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-lg font-bold text-emerald-600">{item.offer.offer_price.toFixed(2)}€</p>
                      {item.savings > 0 && (
                        <p className="text-xs text-emerald-600 flex items-center justify-end gap-1">
                          <Percent className="w-3 h-3" />
                          {item.savings_percent}% günstiger
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {discountOffers.length > 5 && (
                <p className="text-xs text-center text-slate-500 mt-3">
                  +{discountOffers.length - 5} weitere Angebote
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Wish Announcements Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.64 }}
      >
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-600" />
              Ankündigungen (Wunschseite)
              {announcements.filter(a => a.is_active).length > 0 && (
                <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                  {announcements.filter(a => a.is_active).length} aktiv
                </span>
              )}
            </CardTitle>
            <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Neu
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Neue Ankündigung</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Titel (optional)</Label>
                    <Input
                      placeholder="z.B. Neu im Sortiment!"
                      value={announcementForm.title}
                      onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Nachricht *</Label>
                    <Textarea
                      placeholder="Ihre Ankündigung..."
                      value={announcementForm.message}
                      onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Typ</Label>
                      <Select value={announcementForm.type} onValueChange={(v) => setAnnouncementForm({...announcementForm, type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info (blau)</SelectItem>
                          <SelectItem value="success">Erfolg (grün)</SelectItem>
                          <SelectItem value="warning">Hinweis (gelb)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Anzeigen auf</Label>
                      <Select value={announcementForm.target_page} onValueChange={(v) => setAnnouncementForm({...announcementForm, target_page: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wishes">Wunschseite</SelectItem>
                          <SelectItem value="problem">Problemseite</SelectItem>
                          <SelectItem value="both">Beide Seiten</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Gültig ab</Label>
                      <Input
                        type="date"
                        value={announcementForm.valid_from}
                        onChange={(e) => setAnnouncementForm({...announcementForm, valid_from: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Gültig bis (leer = unbegrenzt)</Label>
                      <Input
                        type="date"
                        value={announcementForm.valid_until}
                        onChange={(e) => setAnnouncementForm({...announcementForm, valid_until: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleCreateAnnouncement}
                    disabled={!announcementForm.message}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Erstellen
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {announcements.length === 0 ? (
              <p className="text-slate-500 text-center py-4 text-sm">
                Keine Ankündigungen. Erstellen Sie eine, um sie auf der öffentlichen Wunschseite anzuzeigen.
              </p>
            ) : (
              <div className="space-y-2">
                {announcements.map((ann) => (
                  <div 
                    key={ann.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      ann.is_active 
                        ? 'bg-white border-blue-200' 
                        : 'bg-slate-50 border-slate-200 opacity-60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {ann.title && (
                          <span className="font-medium text-slate-900 text-sm">{ann.title}</span>
                        )}
                        <Badge variant="outline" className={`text-xs ${
                          ann.type === 'success' ? 'border-emerald-500 text-emerald-600' :
                          ann.type === 'warning' ? 'border-amber-500 text-amber-600' :
                          'border-blue-500 text-blue-600'
                        }`}>
                          {ann.type === 'success' ? 'Erfolg' : ann.type === 'warning' ? 'Hinweis' : 'Info'}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {ann.target_page === 'both' ? 'Beide' : ann.target_page === 'problem' ? 'Problem' : 'Wünsche'}
                        </Badge>
                        {!ann.is_active && (
                          <Badge variant="outline" className="text-xs border-slate-400 text-slate-500">Deaktiviert</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 truncate mt-1">{ann.message}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {ann.valid_from} {ann.valid_until ? `bis ${ann.valid_until}` : '(unbegrenzt)'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className={`h-8 w-8 ${ann.is_active ? 'text-amber-600' : 'text-emerald-600'}`}
                        onClick={() => handleToggleAnnouncement(ann.id, ann.is_active)}
                        title={ann.is_active ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {ann.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8 text-rose-600"
                        onClick={() => handleDeleteAnnouncement(ann.id)}
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.65 }}
      >
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">
              Schnellzugriff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/auffuellen">
                <Button className="bg-rose-600 hover:bg-rose-700 btn-hover" data-testid="quick-refill-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Auffüllen
                </Button>
              </Link>
              <Link to="/automat">
                <Button variant="outline" className="btn-hover" data-testid="quick-automat-btn">
                  Automat anzeigen
                </Button>
              </Link>
              <Link to="/kasse">
                <Button variant="outline" className="btn-hover" data-testid="quick-cash-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Kasse
                </Button>
              </Link>
              <Link to="/produkte">
                <Button variant="outline" className="btn-hover" data-testid="quick-products-btn">
                  <Package className="w-4 h-4 mr-2" />
                  Produkte verwalten
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
